import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-checkbox.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ExamDay } from '../../db/examdays.js';
import { ILSUser } from '../../db/ils-user.js';

@customElement('ils-missing-trainees-dialog')
export class ILSMissingTraineesDialog extends XDialog<Boolean> {
  static override get element_name() {
    return 'ils-missing-trainees-dialog';
  }
  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          minWidth: '800px',
        },
      },
    ];
  }

  @state() examDayID: string = '';
  @state() candidates: ILSUser[] = [];
  @state() selectedCandidates: ILSUser[] = [];

  override render() {
    return permissionGuard({
      resource: 'examdays',
      action: 'modifymissingcandidates',
      guardedTemplate: this._content(),
      forbiddenTemplate: html`
        <x-flex padding="md" space="md" ${css({ minWidth: '400px' })}>
          <x-h1> Keine Berechtigung </x-h1>
          <x-button mode="outline" @click=${() => this.close(false)}> Abbrechen </x-button>
        </x-flex>
      `,
    });
  }

  private _content() {
    const sortedCandidates = this.candidates.sort((a, b) => a.ecommerce!.fullName.localeCompare(b.ecommerce!.fullName));
    return html`
      <x-flex padding="md" space="md" ${css({ minWidth: '400px' })}>
        <x-h1> Fehlende Teilnehmer </x-h1>
        <x-dropdown-multiselection
          .value=${this.selectedCandidates}
          placeholder="Prüflinge"
          .selectables=${sortedCandidates || 'empty'}
          .renderString=${(item: ILSUser | 'empty') => this.renderStringForMultiselection(item)}
          .renderItem=${(x: ILSUser, index: number) => html`
            <x-flex ${css({ padding: `${this.spacing.sm} ${this.spacing.md}` })}>
              <x-checkbox
                disabled
                .value=${this.selectedCandidates.includes(x)}
                label=${this.renderStringForMultiselection(x)}
              ></x-checkbox>
            </x-flex>
          `}
          @value-picked=${(e: ValueEvent<ILSUser[] | 'empty'>) => {
            if (e.detail.value !== 'empty') this.selectedCandidates = e.detail.value as ILSUser[];
          }}
        ></x-dropdown-multiselection>
        <x-flex horizontal space-between space="md">
          <x-button mode="outline" tone="error" @click=${() => this.close(false)}> Abbrechen </x-button>
          <x-button
            @click=${async () => {
              try {
                await ExamDay.updateMissingCandidates(
                  this.examDayID,
                  this.selectedCandidates.map(c => c._id),
                )();
                this.close(true);
              } catch (e) {
                logger.error(e);
              }
            }}
            ${css({ marginLeft: 'auto' })}
          >
            Speichern
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private renderStringForMultiselection(item: ILSUser | 'empty') {
    if (item === 'empty') return 'Keine Nutzer angegeben.';

    return item.ecommerce?.fullName;
  }
}
